<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { diffLines, summarizeDiff, type LineDiffSegment } from '../../../lib/text/line-diff'
import AppScrollArea from '../AppScrollArea/AppScrollArea.vue'

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    before: string
    after: string
    /** Caps the rendered height so a long diff scrolls inside its own box rather than the page. */
    maxHeight?: 'sm' | 'md' | 'none'
  }>(),
  {
    maxHeight: 'md',
  },
)

const segments = computed<LineDiffSegment[]>(() => diffLines(props.before, props.after))

const summary = computed(() => summarizeDiff(segments.value))

const summaryLabel = computed(() =>
  t('common.diff.summary', {
    removed: summary.value.removed,
    added: summary.value.added,
  }),
)

const SEGMENT_CLASSES: Record<LineDiffSegment['kind'], string> = {
  same: 'text-text-tertiary',
  removed: 'bg-accent-red/10 text-accent-red',
  added: 'bg-accent-green/10 text-accent-green',
}

const SEGMENT_MARKERS: Record<LineDiffSegment['kind'], string> = {
  same: ' ',
  removed: '-',
  added: '+',
}

const MAX_HEIGHT_CLASSES: Record<'sm' | 'md' | 'none', string> = {
  sm: 'max-h-(--diff-max-height-sm)',
  md: 'max-h-(--diff-max-height-md)',
  none: '',
}
</script>

<template>
  <div class="flex min-w-0 flex-col gap-4">
    <p class="text-caption text-text-tertiary">{{ summaryLabel }}</p>
    <div class="flex min-w-0 flex-col rounded-4 border border-border bg-bg-surface">
      <AppScrollArea class="min-w-0 py-4" :class="MAX_HEIGHT_CLASSES[maxHeight]">
        <p
          v-for="(segment, segmentIndex) in segments"
          :key="segmentIndex"
          :class="[
            'px-8 text-body-sm whitespace-pre-wrap break-words',
            SEGMENT_CLASSES[segment.kind],
          ]"
        >
          <span v-for="(line, lineIndex) in segment.lines" :key="lineIndex" class="block">
            <span aria-hidden="true" class="select-none">{{ SEGMENT_MARKERS[segment.kind] }} </span
            >{{ line }}
          </span>
        </p>
      </AppScrollArea>
    </div>
  </div>
</template>
